import { FC } from 'react'
import { When } from 'react-if'
import LazyHydrate from 'react-lazy-hydration'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { paths } from '@/constants/paths'
import { MoviesAndSeriesCollection } from '@/molecules/MoviesAndSeriesCollection/MoviesAndSeriesCollection'
import { WatchNowCollection } from '@/organisms/WatchNowCollection'
import { TopPicksForYouCollection } from '@/organisms/WatchNowCollection/TopPicksForYouCollection'
import { WatchlistCollection } from '@/organisms/WatchlistCollection'
import { LargePortraitGrid } from '@/page/CollectionDisplay/LargePortraitGrid'
import { ComingSoonCollection } from '@/page/ComingSoonCollection'
import { GuildUpdatesPortraitRowCollection } from '@/page/GuildUpdatesPortraitRowCollection'
import { LivestreamLandscapeRowCollection } from '@/page/LivestreamLandscapeRowCollection'
import { NewOnAngelCollection } from '@/page/NewUpcomingCollection/NewOnAngelCollection'
import { UpcomingEpisodesSpecialsCollection } from '@/page/NewUpcomingCollection/UpcomingEpisodesSpecialsCollection'
import { SneakPeekCollection } from '@/page/SneakPeekCollection'
import { useGuildMember } from '@/services/GuildUserService'
import { LinkViewModel, PageDataContext } from '@/services/RenderService/types'
import { useUser } from '@/services/UserService'
import { Collection as CollectionModel } from '@/types/codegen-contentful'
import { ContentMovie, ContentSeries } from '@/types/codegen-federation'
import { isDefined } from '@/utils/primitives/generics'
import { TranslateFunction, useTranslate } from '@/utils/translate/translate-client'

interface Props {
  collection: CollectionModel
  pageDataContext: PageDataContext
}

const renderSquareCollection = (pageDataContext: PageDataContext, isGuildMember: boolean, isLoggedIn: boolean) => (
  <LazyHydrate whenVisible>
    <>
      <When condition={isGuildMember}>{null}</When>
      <When condition={!isGuildMember}>
        <When condition={isLoggedIn}>
          <PaddedContainer className="mb-12 overflow-x-hidden" fluid>
            <WatchlistCollection />
          </PaddedContainer>
          <PaddedContainer className="mb-12 overflow-x-hidden" fluid>
            <TopPicksForYouCollection />
          </PaddedContainer>
          <PaddedContainer className="mb-12 overflow-x-hidden" fluid>
            <MoviesAndSeriesCollection contentLinks={pageDataContext['brand-logos']} />
          </PaddedContainer>
          <WatchNowCollection contentLinks={pageDataContext['start-watching'] ?? []} title={''} onlyShowContinue />
        </When>
      </When>
      <When condition={!isLoggedIn}>
        <PaddedContainer className="overflow-x-hidden" fluid>
          <MoviesAndSeriesCollection contentLinks={pageDataContext['brand-logos']} />
        </PaddedContainer>
      </When>
    </>
  </LazyHydrate>
)

const renderPortraitGridCollection = (collection: CollectionModel, pageDataContext: PageDataContext) => {
  if (pageDataContext['all-movies'] || pageDataContext['all-shows']) {
    const contentData = pageDataContext['all-movies'] || pageDataContext['all-shows']
    const mappedLinks = mapContentToLinks(contentData)

    return <LargePortraitGrid links={mappedLinks} id={collection?.title ?? ''} />
  }

  return (
    <ComingSoonCollection
      ComingSoonTabCollection={pageDataContext['coming-soon-tab'] ?? []}
      title={collection.title ?? ''}
      testId="coming-soon-collection"
      layout="portrait-grid"
    />
  )
}

const renderLandscapeRowCollection = (
  collection: CollectionModel,
  pageDataContext: PageDataContext,
  isGuildMember: boolean,
  t: TranslateFunction,
) => {
  switch (collection.dataSource) {
    case 'start-watching':
      return (
        <>
          <LazyHydrate whenVisible>
            <When condition={isGuildMember}>
              <PaddedContainer className="mb-12 overflow-x-hidden" fluid>
                <WatchlistCollection />
              </PaddedContainer>
              <PaddedContainer className="mb-12 overflow-x-hidden" fluid>
                <TopPicksForYouCollection />
              </PaddedContainer>
              <PaddedContainer className="mb-12 overflow-x-hidden" fluid>
                <MoviesAndSeriesCollection contentLinks={pageDataContext['brand-logos']} />
              </PaddedContainer>
              <WatchNowCollection
                contentLinks={pageDataContext['start-watching'] ?? []}
                title={collection.title ?? ''}
                onlyShowContinue
              />
            </When>
          </LazyHydrate>
        </>
      )
    case 'continue-watching':
      return (
        <WatchNowCollection contentLinks={pageDataContext['start-watching'] ?? []} title={collection.title ?? ''} />
      )
    case 'early-access':
      return (
        <>
          <When condition={!isGuildMember}>
            <PaddedContainer className="overflow-x-hidden" fluid>
              <SneakPeekCollection
                title={t('startWatchingForFree', 'Start Watching for Free')}
                sneakPeekTitleCollection={pageDataContext['sneak-peeks']}
                testId="sneak-peek-collection"
              />
            </PaddedContainer>
          </When>
        </>
      )
    case 'new-on-angel':
      return (
        <LazyHydrate whenVisible>
          <PaddedContainer className="overflow-x-hidden" fluid>
            <NewOnAngelCollection testId="new-on-angel-collection" title={collection.title ?? ''} enableWatchProgress />
          </PaddedContainer>
        </LazyHydrate>
      )
    case 'upcoming-episodes-specials':
      return (
        <LazyHydrate whenVisible>
          <PaddedContainer className="overflow-x-hidden" fluid>
            <UpcomingEpisodesSpecialsCollection
              testId="upcoming-episodes-specials-collection"
              title={collection.title ?? ''}
              ComingSoonTabCollection={pageDataContext['coming-soon-tab'] ?? []}
              enableWatchProgress
            />
          </PaddedContainer>
        </LazyHydrate>
      )
    case 'livestreams':
      return (
        <PaddedContainer className="overflow-x-hidden" fluid>
          <LivestreamLandscapeRowCollection testId="livestreams-collection" title={collection.title ?? ''} />
        </PaddedContainer>
      )
    default:
      return null
  }
}

const renderPortraitRowCollection = (collection: CollectionModel, pageDataContext: PageDataContext) => {
  switch (collection.dataSource) {
    case 'guild-updates':
      return (
        <PaddedContainer className="overflow-x-hidden" fluid>
          <GuildUpdatesPortraitRowCollection testId="livestreams-collection" title={collection.title ?? ''} />
        </PaddedContainer>
      )
    case 'coming-soon':
      return (
        <LazyHydrate whenVisible>
          <PaddedContainer className="overflow-x-hidden" fluid>
            <ComingSoonCollection
              ComingSoonTabCollection={pageDataContext['coming-soon-tab'] ?? []}
              title={collection.title ?? ''}
              testId="coming-soon-collection"
              layout="portrait-row"
            />
          </PaddedContainer>
        </LazyHydrate>
      )
    default:
      return null
  }
}

export const Collection: FC<Props> = ({ collection, pageDataContext }) => {
  const { isGuildMember } = useGuildMember()
  const { isLoggedIn } = useUser()
  const { t } = useTranslate('watch')

  if (!collection?.dataSource) return null

  switch (collection.displayAs) {
    case 'square':
      return renderSquareCollection(pageDataContext, isGuildMember, isLoggedIn)
    case 'portrait-grid':
      return renderPortraitGridCollection(collection, pageDataContext)
    case 'landscape-row':
      return renderLandscapeRowCollection(collection, pageDataContext, isGuildMember, t)
    case 'portrait-row':
      return renderPortraitRowCollection(collection, pageDataContext)
    default:
      return null
  }
}

export const mapContentToLinks = (
  contentData: ContentMovie[] | ContentSeries[] | null | undefined,
): LinkViewModel[] => {
  return (contentData ?? [])
    .map((item) => {
      if (!item?.project?.slug || !item.image?.cloudinaryPath) return null

      return {
        slug: item.project.slug,
        title: item.project.title ?? '',
        imageUrl: item.image.cloudinaryPath,
        linkUrl: `${paths.watch.index}/${item.project.slug}`,
      }
    })
    .filter(isDefined)
}
